// ─── PvpTabletLayout.js ───────────────────────────────────────────────────────
import { useState } from "react";
import { Chessboard } from "react-chessboard";
import { SHARED_DECK, ASURA_DECK, ASURA_CARD_EMOJI, CARD_EMOJI } from "./gameConstants";
import { formatTime, getPieceSymbol } from "./gameUtils";

// ── Which deck does each colour use? ─────────────────────────────────────────
const DECK_FOR = { w: SHARED_DECK, b: ASURA_DECK };
const EMOJI_FOR = { w: CARD_EMOJI, b: ASURA_CARD_EMOJI };

// ── CardTooltip ───────────────────────────────────────────────────────────────
function CardTooltip({ card, emoji, cost, onClose, onCancel, flipped }) {
  if (!card) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          transform: flipped ? "rotate(180deg)" : "none",
          backgroundColor: "#0f172a",
          border: `2px solid ${card.color}`,
          borderRadius: "20px",
          padding: "24px 28px",
          maxWidth: "320px",
          width: "85vw",
          boxShadow: `0 0 40px ${card.color}66`,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          alignItems: "center",
        }}
      >
        {/* Card image */}
        <div
          style={{
            width: "96px",
            height: "96px",
            borderRadius: "14px",
            overflow: "hidden",
            border: `3px solid ${card.color}`,
            boxShadow: `0 0 20px ${card.color}88`,
            flexShrink: 0,
          }}
        >
          <img
            src={card.image}
            alt={card.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>

        {/* Name + emoji */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "22px", fontWeight: "800", color: "#f1f5f9", letterSpacing: "0.02em" }}>
            {emoji} {card.name}
          </div>
          <div
            style={{
              display: "inline-block",
              marginTop: "6px",
              backgroundColor: `${card.color}22`,
              border: `1px solid ${card.color}66`,
              borderRadius: "20px",
              padding: "3px 12px",
              fontSize: "12px",
              fontWeight: "700",
              color: card.color,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Tier {card.tier}
          </div>
        </div>

        {/* Description */}
        <p
          style={{
            color: "#cbd5e1",
            fontSize: "15px",
            lineHeight: "1.55",
            textAlign: "center",
            margin: 0,
          }}
        >
          {card.description}
        </p>

        {/* Cost + Tap instruction + Cancel */}
        <div style={{ display: "flex", gap: "8px", width: "100%", marginTop: "4px" }}>
          {/* Cost */}
          <div
            style={{
              flex: 1,
              textAlign: "center",
              backgroundColor: "rgba(255,215,0,0.1)",
              border: "1px solid rgba(255,215,0,0.3)",
              borderRadius: "10px",
              padding: "10px 0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "900", color: "#ffd700" }}>{cost}s</div>
            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>time cost</div>
          </div>

          {/* Tap a piece instruction */}
          <div
            onClick={onClose}
            style={{
              flex: 1,
              textAlign: "center",
              backgroundColor: "rgba(78,204,163,0.1)",
              border: "1px solid rgba(78,204,163,0.3)",
              borderRadius: "10px",
              padding: "10px 6px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#4ecca3" }}>👆 Tap a piece</div>
            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>to place card</div>
          </div>

          {/* Cancel */}
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              backgroundColor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "10px",
              color: "#94a3b8",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              padding: "10px 0",
            }}
          >
            ✕ Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LastCardBanner ────────────────────────────────────────────────────────────
function LastCardBanner({ lastCard, lastCardEmoji, opponentColor, flipped }) {
  if (!lastCard) return null;
  const label = opponentColor === "w" ? "⬜ White played" : "⬛ Black played";
  return (
    <div
      style={{
        width: "100%",
        transform: flipped ? "rotate(180deg)" : "none",
        padding: "6px 14px",
        backgroundColor: `${lastCard.color}14`,
        borderTop: flipped ? "none" : `1px solid ${lastCard.color}44`,
        borderBottom: flipped ? `1px solid ${lastCard.color}44` : "none",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        minHeight: "38px",
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "6px",
          overflow: "hidden",
          border: `1px solid ${lastCard.color}88`,
          flexShrink: 0,
        }}
      >
        <img
          src={lastCard.image}
          alt={lastCard.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "10px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "1px" }}>
          {label}
        </div>
        <div style={{ fontSize: "13px", fontWeight: "700", color: lastCard.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {lastCardEmoji} {lastCard.name}
        </div>
        <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {lastCard.description.split(" ").slice(0, 7).join(" ")}…
        </div>
      </div>
    </div>
  );
}

// ── PvpCardTray ───────────────────────────────────────────────────────────────
function PvpCardTray({
  currentTurn,
  myColor,
  tier1Unlocked,
  tier2Unlocked,
  tier3Unlocked,
  selectedCard,
  onSelectCard,
  usedCards,
  getCardCost,
  lastOpponentCard,
  flipped,
}) {
  const [tooltipCard, setTooltipCard] = useState(null);

  const isMyTurn = currentTurn === myColor;
  const accent = myColor === "w" ? "#4ecca3" : "#e94560";
  const deck = DECK_FOR[myColor];
  const emojiMap = EMOJI_FOR[myColor];
  const opponentColor = myColor === "w" ? "b" : "w";

  const lastCardObj = lastOpponentCard
    ? DECK_FOR[opponentColor].find(c => c.id === lastOpponentCard.id) ?? null
    : null;
  const lastCardEmoji = lastCardObj ? EMOJI_FOR[opponentColor][lastCardObj.id] : null;

  function handleCardTap(card) {
    if (!isMyTurn) return;
    const tierNum = card.tier;
    const isUnlocked =
      (tierNum === 1 && tier1Unlocked) ||
      (tierNum === 2 && tier2Unlocked) ||
      (tierNum === 3 && tier3Unlocked);
    if (!isUnlocked) return;
    if (usedCards.includes(card.id)) return;
    onSelectCard(card);
    setTooltipCard(card);
  }

  // Closes overlay but KEEPS card selected — next tap goes to the board
  function handleTooltipClose() {
    setTooltipCard(null);
  }

  // Cancel button — fully deselects the card
  function handleTooltipCancel() {
    setTooltipCard(null);
    onSelectCard(null);
  }

  // Card strip (shared between flipped and non-flipped renders)
  const trayContent = (
    <div
      style={{
        width: "100%",
        padding: "8px 12px",
        backgroundColor: isMyTurn ? `${accent}18` : "rgba(0,0,0,0.3)",
        borderTop: myColor === "w" ? `2px solid ${isMyTurn ? accent : "rgba(255,255,255,0.08)"}` : "none",
        borderBottom: myColor === "b" ? `2px solid ${isMyTurn ? accent : "rgba(255,255,255,0.08)"}` : "none",
        transition: "background-color 0.4s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "700",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: isMyTurn ? accent : "rgba(255,255,255,0.6)",
            minWidth: "60px",
            whiteSpace: "pre-line",
          }}
        >
          {isMyTurn ? "▶ YOUR\nTURN" : myColor === "w" ? "⬜ White" : "⬛ Black"}
        </div>

        <div style={{ display: "flex", gap: "5px", flex: 1, justifyContent: "center" }}>
          {deck.map(card => {
            const tierNum = card.tier;
            const isUnlocked =
              (tierNum === 1 && tier1Unlocked) ||
              (tierNum === 2 && tier2Unlocked) ||
              (tierNum === 3 && tier3Unlocked);
            const isUsed = usedCards.includes(card.id);
            const isSelected = selectedCard?.id === card.id;
            const canUse = isUnlocked && !isUsed && isMyTurn;
            const cost = getCardCost(card);
            const emoji = emojiMap[card.id] ?? "✨";

            return (
              <div
                key={card.id}
                onClick={() => handleCardTap(card)}
                style={{
                  position: "relative",
                  width: "60px",
                  height: "60px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: isSelected
                    ? "2px solid #fff"
                    : isUnlocked && !isUsed
                      ? `2px solid ${card.color}66`
                      : "2px solid rgba(255,255,255,0.06)",
                  opacity: isUsed ? 0.3 : isUnlocked ? 1 : 0.25,
                  cursor: canUse ? "pointer" : "default",
                  boxShadow: isSelected ? `0 0 12px ${card.color}` : "none",
                  flexShrink: 0,
                  transition: "box-shadow 0.2s, border-color 0.2s",
                }}
              >
                {isUnlocked ? (
                  <>
                    <img
                      src={card.image}
                      alt={card.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: isUsed ? "grayscale(100%)" : "none",
                      }}
                    />
                    {!isUsed && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "2px",
                          right: "2px",
                          backgroundColor: "rgba(0,0,0,0.85)",
                          color: "#ffd700",
                          fontSize: "11px",
                          fontWeight: "bold",
                          padding: "1px 3px",
                          borderRadius: "3px",
                        }}
                      >
                        {cost}s
                      </div>
                    )}
                    {!isUsed && (
                      <div
                        style={{
                          position: "absolute",
                          top: "2px",
                          left: "2px",
                          fontSize: "12px",
                          lineHeight: 1,
                          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.9))",
                        }}
                      >
                        {emoji}
                      </div>
                    )}
                    {isUsed && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "18px",
                          color: "rgba(255,255,255,0.4)",
                        }}
                      >
                        ✕
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#0f172a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                    }}
                  >
                    🔒
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── Black's tray (flipped=true) ──
  // Tooltip rendered OUTSIDE the rotate wrapper so it doesn't get double-rotated
  if (flipped) {
    return (
      <>
        <div style={{ transform: "rotate(180deg)", width: "100%" }}>
          {trayContent}
        </div>
        <LastCardBanner
          lastCard={lastCardObj}
          lastCardEmoji={lastCardEmoji}
          opponentColor={opponentColor}
          flipped={true}
        />
        {tooltipCard && (
          <CardTooltip
            card={tooltipCard}
            emoji={emojiMap[tooltipCard.id] ?? "✨"}
            cost={getCardCost(tooltipCard)}
            onClose={handleTooltipClose}
            onCancel={handleTooltipCancel}
            flipped={true}
          />
        )}
      </>
    );
  }

  // ── White's tray (flipped=false) ──
  return (
    <>
      <LastCardBanner
        lastCard={lastCardObj}
        lastCardEmoji={lastCardEmoji}
        opponentColor={opponentColor}
        flipped={false}
      />
      {trayContent}
      {tooltipCard && (
        <CardTooltip
          card={tooltipCard}
          emoji={emojiMap[tooltipCard.id] ?? "✨"}
          cost={getCardCost(tooltipCard)}
          onClose={handleTooltipClose}
          onCancel={handleTooltipCancel}
          flipped={false}
        />
      )}
    </>
  );
}

// ── PvpTabletLayout ───────────────────────────────────────────────────────────
export default function PvpTabletLayout({
  game,
  gameOver,
  gameOverDismissed,
  setGameOverDismissed,
  winner,
  theme,
  whiteTime,
  blackTime,
  customStyles,
  onPieceDrop,
  onSquareClick,
  tier1Unlocked,
  tier2Unlocked,
  tier3Unlocked,
  selectedCard,
  setSelectedCard,
  whiteUsedCards,
  blackUsedCards,
  usedCards,
  getCardCost,
  whiteCaptured,
  blackCaptured,
  specialModeLabel,
  setChandraPlacementMode,
  chandraPlacementMode,
  setGuruMode,
  setShaniMode,
  resetGame,
  showChaosPopup,
  setShowChaosPopup,
  lastWhiteCard,
  lastBlackCard,
  guruDuplicateMode,
  setGuruDuplicateMode,
  mahishasuraMode,
  setMahishasuraMode,
  confirmMahishaShift,
  confirmChandraPlacement,
  promotionPending,
  onPromotionChoice,
}) {
  const currentTurn = game.turn();
  const tabletBoard = Math.min(window.innerWidth - 24, 480);
  const customPieces = {};
  ["wP", "wN", "wB", "wR", "wQ", "wK", "bP", "bN", "bB", "bR", "bQ", "bK"].forEach(piece => {
    customPieces[piece] = ({ squareWidth }) => (
      <div style={{
        width: squareWidth,
        height: squareWidth,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: squareWidth * 0.88,
        transform: piece.startsWith("b") ? "rotate(180deg)" : "none",
        userSelect: "none",
        filter: piece.startsWith("w")
          ? "drop-shadow(0px 1px 2px rgba(0,0,0,0.9)) drop-shadow(0px 0px 6px rgba(0,0,0,0.6))"
          : "drop-shadow(0px 0px 1px rgba(233,69,96,0.8)) drop-shadow(0px 0px 3px rgba(233,69,96,0.4))",
      }}>
        {({
          wK: "♔", wQ: "♕", wR: "♖", wB: "♗", wN: "♘", wP: "♙",
          bK: "♚", bQ: "♛", bR: "♜", bB: "♝", bN: "♞", bP: "♟",
        })[piece]}
      </div>
    );
  });
  const wUsed = whiteUsedCards ?? usedCards ?? [];
  const bUsed = blackUsedCards ?? usedCards ?? [];
  const [confirmMenu, setConfirmMenu] = useState(false);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          height: "100vh",
          width: "100%",
          backgroundColor: theme.background,
          color: theme.text,
          userSelect: "none",
          overscrollBehavior: "none",
          justifyContent: "space-between",
        }}
      >
        {/* ── Chaos popup ── */}
        {showChaosPopup && (
          <div
            onClick={() => setShowChaosPopup(false)}
            style={{
              position: "fixed",
              top: "16px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#ff6b6b",
              padding: "10px 24px",
              borderRadius: "12px",
              zIndex: 600,
              textAlign: "center",
              border: "2px solid #fff",
              cursor: "pointer",
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: "15px" }}>
              🔥 CHAOS MODE — All cards half price! 🔥
            </div>
          </div>
        )}

        {/* ── Game over overlay ── */}
        {gameOver && !gameOverDismissed && (
          <div
            onClick={() => setGameOverDismissed(true)}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              backgroundColor: "#16213e",
              padding: "32px 24px",
              borderRadius: "15px",
              zIndex: 700,
              textAlign: "center",
              border: `3px solid ${theme.accent}`,
              width: "88vw",
              maxWidth: "380px",
            }}
          >
            <h2 style={{ marginBottom: "16px", fontSize: "24px" }}>
              {winner === "white" ? "⬜ White Wins!" : "⬛ Black Wins!"}
            </h2>
            <button
              onClick={resetGame}
              style={{
                padding: "14px 28px",
                fontSize: "16px",
                backgroundColor: theme.accent,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                color: "#000",
                fontWeight: "bold",
              }}
            >
              Main Menu
            </button>
            <p style={{ marginTop: "12px", fontSize: "11px", color: "#888" }}>
              or tap to review the board
            </p>
          </div>
        )}

        {/* ── Dismissed game-over pill ── */}
        {gameOver && gameOverDismissed && (
          <div
            onClick={() => setGameOverDismissed(false)}
            style={{
              position: "fixed",
              top: "12px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: theme.accent,
              color: "#000",
              padding: "8px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "bold",
              cursor: "pointer",
              zIndex: 300,
              boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
            }}
          >
            {winner === "white" ? "⬜ White won!" : "⬛ Black won!"} · tap to see result
          </div>
        )}

        {/* ── Mahishasura shapeshift picker ── */}
        {mahishasuraMode?.availableTypes?.length > 0 && (
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", backgroundColor: "#0f172a", border: "2px solid #2D5A1B", borderRadius: "16px", padding: "20px 24px", zIndex: 1000, textAlign: "center", boxShadow: "0 0 40px rgba(45,90,27,0.5)", width: "88vw", maxWidth: "340px" }}>
            <div style={{ transform: currentTurn === "b" ? "rotate(180deg)" : "none" }}>
              <div style={{ fontSize: "16px", fontWeight: "bold", color: "#4ade80", marginBottom: "8px" }}>🐃 Mahishasura — Shapeshift</div>
              <div style={{ fontSize: "13px", color: "#aaa", marginBottom: "16px" }}>Choose a captured piece type to become:</div>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                {mahishasuraMode.availableTypes.map(type => (
                  <button key={type} onClick={() => confirmMahishaShift(type)} style={{ padding: "14px 20px", fontSize: "28px", backgroundColor: "rgba(45,90,27,0.3)", border: "2px solid #4ade80", borderRadius: "12px", cursor: "pointer", color: "#fff" }}>
                    {({ p: "♟", n: "♞", b: "♝", r: "♜", q: "♛" })[type]}
                  </button>
                ))}
              </div>
              <button onClick={() => setMahishasuraMode(null)} style={{ marginTop: "16px", fontSize: "12px", background: "none", border: "none", color: "#aaa", cursor: "pointer", textDecoration: "underline" }}>cancel</button>
            </div>
          </div>
        )}

        {/* ── Chandra confirm placement ── */}
        {chandraPlacementMode?.mirages?.length > 0 && (
          <div style={{ position: "fixed", bottom: "110px", left: "50%", transform: "translateX(-50%)", backgroundColor: "#0f172a", border: "2px solid #e5e7eb", borderRadius: "14px", padding: "14px 18px", zIndex: 1000, textAlign: "center", width: "88vw", maxWidth: "360px" }}>
            <div style={{ transform: currentTurn === "b" ? "rotate(180deg)" : "none" }}>
              <div style={{ fontSize: "13px", color: "#e5e7eb", marginBottom: "10px" }}>
                🌙 {chandraPlacementMode.mirages.length} clone{chandraPlacementMode.mirages.length > 1 ? "s" : ""} placed — teleport real piece?
              </div>
              <div style={{ fontSize: "11px", color: "#aaa", marginBottom: "6px" }}>
                Teleport to: <span style={{ color: "#ffd700" }}>{chandraPlacementMode.teleportTo || "(stay)"}</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", justifyContent: "center", marginBottom: "10px" }}>
                {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map(file => {
                  const sq = file + chandraPlacementMode.rank;
                  const isCurrentReal = sq === chandraPlacementMode.square;
                  const isMirage = chandraPlacementMode.mirages.includes(sq);
                  const hasOtherPiece = game.get(sq) && !isCurrentReal;
                  if (chandraPlacementMode.piece.type === 'b') {
                    const files2 = ["a", "b", "c", "d", "e", "f", "g", "h"];
                    const of2 = files2.indexOf(chandraPlacementMode.square[0]);
                    const oLight = (of2 + chandraPlacementMode.rank) % 2 === 0;
                    const cf2 = files2.indexOf(file);
                    if (oLight !== ((cf2 + chandraPlacementMode.rank) % 2 === 0)) return null;
                  }
                  const disabled = isCurrentReal || isMirage || hasOtherPiece;
                  const isSelected = sq === chandraPlacementMode.teleportTo;
                  return (
                    <button key={file} onClick={() => {
                      if (!disabled) setChandraPlacementMode(prev => ({ ...prev, teleportTo: prev.teleportTo === sq ? null : sq }));
                    }} style={{
                      padding: "5px 8px", fontSize: "11px",
                      backgroundColor: isSelected ? "#ffd700" : disabled ? "#333" : "#555",
                      color: isSelected ? "#000" : "#fff",
                      border: isCurrentReal ? "1px solid #ffd700" : "none",
                      borderRadius: "4px",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.4 : 1,
                    }}>
                      {file}{chandraPlacementMode.rank}
                    </button>
                  );
                })}
              </div>
              <button onClick={confirmChandraPlacement} style={{ padding: "8px 20px", backgroundColor: "#e5e7eb", color: "#000", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", marginRight: "8px" }}>✓ Confirm</button>
              <button onClick={() => setChandraPlacementMode(null)} style={{ padding: "8px 16px", backgroundColor: "transparent", color: "#aaa", border: "1px solid #555", borderRadius: "8px", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        )}

        {guruDuplicateMode && !guruDuplicateMode.side && (
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", backgroundColor: "#0f172a", border: "2px solid #a855f7", borderRadius: "16px", padding: "20px 24px", zIndex: 1000, textAlign: "center", boxShadow: "0 0 40px rgba(168,85,247,0.5)", width: "88vw", maxWidth: "340px" }}>
            <div style={{ transform: currentTurn === "b" ? "rotate(180deg)" : "none" }}>
              <div style={{ fontSize: "16px", fontWeight: "bold", color: "#a855f7", marginBottom: "8px" }}>🪐 Guru — Duplicate</div>
              <div style={{ fontSize: "13px", color: "#aaa", marginBottom: "20px" }}>Spawn duplicate to the left or right?</div>
              <div style={{ display: "flex", gap: "16px", justifyContent: "center" }}>
                <button onClick={() => setGuruDuplicateMode(prev => ({ ...prev, side: "left" }))}
                  style={{ padding: "14px 28px", fontSize: "15px", backgroundColor: "#1e1040", border: "2px solid #a855f7", borderRadius: "10px", cursor: "pointer", color: "#fff", fontWeight: "bold" }}>
                  ← Left
                </button>
                <button onClick={() => setGuruDuplicateMode(prev => ({ ...prev, side: "right" }))}
                  style={{ padding: "14px 28px", fontSize: "15px", backgroundColor: "#1e1040", border: "2px solid #a855f7", borderRadius: "10px", cursor: "pointer", color: "#fff", fontWeight: "bold" }}>
                  Right →
                </button>
              </div>
              <button onClick={() => setGuruDuplicateMode(null)} style={{ marginTop: "16px", fontSize: "12px", background: "none", border: "none", color: "#aaa", cursor: "pointer", textDecoration: "underline" }}>cancel</button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════
            BLACK'S ZONE — rotated 180° at top
        ════════════════════════════════════════ */}
        <div
          style={{
            transform: "rotate(180deg)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "3px 16px 2px",
            backgroundColor: currentTurn === "b" ? "rgba(233,69,96,0.12)" : "transparent",
            transition: "background-color 0.4s",
          }}
        >
          <div style={{ fontSize: "15px", color: "#fff" }}>
            ⬛ Black
            <span style={{ marginLeft: "8px", fontSize: "11px" }}>
              {blackCaptured.slice(-8).map((p, i) => (
                <span key={i}>{getPieceSymbol(p)}</span>
              ))}
            </span>
          </div>
          <div
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: blackTime < 30 ? "#ff6b6b" : theme.text,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTime(blackTime)}
          </div>
        </div>

        <PvpCardTray
          currentTurn={currentTurn}
          myColor="b"
          tier1Unlocked={tier1Unlocked}
          tier2Unlocked={tier2Unlocked}
          tier3Unlocked={tier3Unlocked}
          selectedCard={selectedCard}
          onSelectCard={setSelectedCard}
          usedCards={bUsed}
          getCardCost={getCardCost}
          lastOpponentCard={lastWhiteCard ? { id: lastWhiteCard } : null}
          flipped={true}
        />

        {/* ════════════════════════════════════════
            CENTRE — board
        ════════════════════════════════════════ */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "2px 0",
            width: "100%",
          }}
        >
          <div
            style={{
              transform: currentTurn === "b" ? "rotate(180deg)" : "none",
              width: tabletBoard,
            }}
          >
            {specialModeLabel && (
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#1a1a00",
                  border: "1px solid #ffd700",
                  borderRadius: "8px",
                  padding: "7px 12px",
                  marginBottom: "6px",
                  fontSize: "13px",
                  color: "#ffd700",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                {specialModeLabel}
                <button
                  onClick={() => {
                    setSelectedCard(null);
                    setChandraPlacementMode(null);
                    setGuruMode(null);
                    setShaniMode(null);
                    if (setMahishasuraMode) setMahishasuraMode(null);
                  }}
                  style={{
                    fontSize: "11px",
                    background: "none",
                    border: "1px solid #ffd700",
                    borderRadius: "4px",
                    color: "#ffd700",
                    cursor: "pointer",
                    padding: "2px 6px",
                  }}
                >
                  ✕ cancel
                </button>
              </div>
            )}

            <div
              style={{
                width: "100%",
                textAlign: "center",
                fontSize: "12px",
                color: currentTurn === "w" ? "#4ecca3" : "#e94560",
                marginBottom: "4px",
              }}
            >
              {!gameOver && (currentTurn === "w" ? "⬜ White to move" : "⬛ Black to move")}
            </div>
          </div>

          <div id="pvp-board" style={{ width: tabletBoard, height: tabletBoard, flexShrink: 0 }}>
            <Chessboard
              position={game.fen()}
              onPieceDrop={onPieceDrop}
              onSquareClick={onSquareClick}
              animationDuration={225}
              customSquareStyles={customStyles}
              customDarkSquareStyle={{ backgroundColor: theme.darkSquare }}
              customLightSquareStyle={{ backgroundColor: theme.lightSquare }}
              boardWidth={tabletBoard}
              boardOrientation="white"
              customPieces={customPieces} />
          </div>

          <button
            onClick={() => {
              const el = document.documentElement;
              if (el.requestFullscreen) el.requestFullscreen();
              else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
            }}
            style={{
              marginTop: "4px",
              padding: "6px 16px",
              fontSize: "11px",
              backgroundColor: "transparent",
              color: "#dadada",
              border: "1px solid #2a2a2a",
              borderRadius: "20px",
              cursor: "pointer",
            }}
          >
            ⛶ Fullscreen
          </button>
          {confirmMenu ? (
            <div style={{ marginTop: "6px", display: "flex", gap: "8px" }}>
              <button
                onClick={resetGame}
                style={{ padding: "8px 16px", fontSize: "12px", backgroundColor: "#e94560", color: "#fff", border: "none", borderRadius: "20px", cursor: "pointer", fontWeight: "bold" }}
              >
                Yes, quit
              </button>
              <button
                onClick={() => setConfirmMenu(false)}
                style={{ padding: "8px 16px", fontSize: "12px", backgroundColor: "transparent", color: "#dadada", border: "1px solid #333", borderRadius: "20px", cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmMenu(true)}
              style={{ marginTop: "4px", padding: "8px 20px", fontSize: "12px", backgroundColor: "transparent", color: "#dadada", border: "1px solid #333", borderRadius: "20px", cursor: "pointer" }}
            >
              ✕ Menu
            </button>
          )}
        </div>

        {/* ════════════════════════════════════════
            WHITE'S ZONE — normal orientation at bottom
        ════════════════════════════════════════ */}
        <PvpCardTray
          currentTurn={currentTurn}
          myColor="w"
          tier1Unlocked={tier1Unlocked}
          tier2Unlocked={tier2Unlocked}
          tier3Unlocked={tier3Unlocked}
          selectedCard={selectedCard}
          onSelectCard={setSelectedCard}
          usedCards={wUsed}
          getCardCost={getCardCost}
          lastOpponentCard={lastBlackCard ? { id: lastBlackCard } : null}
          flipped={false}
        />

        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "3px 16px 4px",
            backgroundColor: currentTurn === "w" ? "rgba(78,204,163,0.12)" : "transparent",
            transition: "background-color 0.4s",
          }}
        >
          <div style={{ fontSize: "15px", color: "#fff" }}>
            ⬜ White
            <span style={{ marginLeft: "8px", fontSize: "11px" }}>
              {whiteCaptured.slice(-8).map((p, i) => (
                <span key={i}>{getPieceSymbol(p)}</span>
              ))}
            </span>
          </div>
          <div
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: whiteTime < 30 ? "#ff6b6b" : theme.text,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTime(whiteTime)}
          </div>
        </div>
      </div>
      {promotionPending && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 900,
          backgroundColor: "rgba(0,0,0,0.75)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            backgroundColor: "#0f172a", borderRadius: "16px",
            padding: "24px", textAlign: "center",
            border: "2px solid #4ecca3", width: "88vw", maxWidth: "320px",
            transform: game.turn() === "b" ? "rotate(180deg)" : "none"
          }}>
            <div style={{ fontSize: "15px", fontWeight: "bold", color: "#4ecca3", marginBottom: "16px" }}>
              ♟ Promote your pawn
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              {[
                { type: "q", symbol: game.turn() === "w" ? "♕" : "♛", label: "Queen" },
                { type: "r", symbol: game.turn() === "w" ? "♖" : "♜", label: "Rook" },
                { type: "b", symbol: game.turn() === "w" ? "♗" : "♝", label: "Bishop" },
                { type: "n", symbol: game.turn() === "w" ? "♘" : "♞", label: "Knight" },
              ].map(({ type, symbol, label }) => (
                <button key={type} onClick={() => onPromotionChoice(type)} style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  padding: "12px", fontSize: "32px",
                  backgroundColor: "rgba(78,204,163,0.1)",
                  border: "2px solid #4ecca3", borderRadius: "12px",
                  cursor: "pointer", color: "#fff", gap: "4px"
                }}>
                  {symbol}
                  <span style={{ fontSize: "10px", color: "#94a3b8" }}>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}